import type { CalendarSnapshot } from "../catalog.js";
import { editionBoardHref, headedForResult, isHeadedForResult, pickKey, stickySlots } from "../live/hot-boards.js";
import { h } from "./dom.js";
import { bandHeadline, type TopGame } from "./top-games.js";
import "./hot-band.css";

/**
 * The home page's LIVE hot-games band, "Headed for a result" (O3, text cards;
 * layout lane L2, owner-approved 2026-09-22). The tournament page's "Live now"
 * mini-board band went with that page on 2026-09-23: the watch page's Heating up
 * band and its rail's Hot tab show the event's hot games.
 *
 * Its own module, apart from the tournament page's list (`ui/hot-games.ts`), so
 * the home page's entry chunk carries only this: the home band is a
 * self-contained module plus a two-line hook in `pages/home.ts` (#2606, the
 * home-loading work, was open when it landed). DOM only — which picks, and in
 * which order, is `live/hot-boards.ts`'s.
 *
 * Every band keeps a FIXED block size (CLS 0): one scrolling row, holding a
 * sentence while waiting or when empty; or, for the home band, hidden until it
 * is revealed in the same task as the grid above it.
 */

function miniCard(game: TopGame, editionId: string | null): HTMLElement {
  const pin = game.breaking ? "Mate on the board" : isHeadedForResult(game) ? "Headed for a result" : null;
  return h("a", {
    class: "hot-mini",
    href: editionBoardHref(editionId, game.roundId, game.boardId),
    "data-pick": pickKey(game),
    title: `${game.whiteName} vs ${game.blackName}`,
  },
    h("span", { class: "hot-mini-names", text: `${game.whiteName} – ${game.blackName}` }),
    h("span", { class: "hot-mini-meta" },
      game.eventName ? h("span", { class: "hot-mini-event", text: game.eventName }) : null,
      game.roundName ? h("span", { text: game.roundName }) : null,
      pin ? h("span", { class: "hot-band-pin", text: pin }) : null,
    ),
    game.headline ? h("span", { class: "hot-band-headline", text: bandHeadline(game.headline) }) : null,
  );
}

interface LiveBand {
  root: HTMLElement;
  /** The picks to show now, in producer order; the band keeps each one's slot while it stays. */
  update(picks: readonly TopGame[]): void;
}

interface BandOptions {
  className: string;
  titleId: string;
  kicker: string;
  title: string;
  waiting: string;
  empty: string;
  /** The edition a pick's board link names (`editionBoardHref`), when the page knows it. */
  editionOf: (game: TopGame) => string | null;
  /**
   * Start hidden and stay hidden while there is nothing to show. Only for a band
   * revealed in the SAME task as the content above it (the home grid), so its
   * appearance adds no shift of its own.
   */
  hideWhenEmpty?: boolean;
}

/**
 * A band of stream-free live cards with a FIXED block size (CLS 0): it starts
 * holding its waiting sentence, and when the picks come it swaps content inside
 * the same box. An empty answer is a sentence in the same box, never a
 * collapse. Picks keep their slot while they stay ({@link stickySlots}).
 */
function createBand(options: BandOptions): LiveBand {
  const grid = h("div", { class: "hot-mini-grid" }, h("p", { class: "hot-band-note", text: options.waiting }));
  const root = h("section", { class: `hot-band ${options.className}`, "aria-labelledby": options.titleId, "aria-busy": "true" },
    h("div", { class: "hot-band-heading" },
      h("p", { class: "hot-band-kicker", text: options.kicker }),
      h("h2", { id: options.titleId, text: options.title }),
    ),
    grid,
  );
  if (options.hideWhenEmpty) root.hidden = true;
  let order: string[] = [];
  let signature: string | null = null;
  let shown: TopGame[] = [];
  const paint = (): void => {
    grid.replaceChildren(...(shown.length === 0
      ? [h("p", { class: "hot-band-note", text: options.empty })]
      : shown.map(game => miniCard(game, options.editionOf(game)))));
  };
  return {
    root,
    update(picks) {
      root.setAttribute("aria-busy", "false");
      const byKey = new Map(picks.map(game => [pickKey(game), game]));
      order = stickySlots(order, picks.map(pickKey), picks.length);
      shown = order.map(key => byKey.get(key)).filter((game): game is TopGame => game !== undefined);
      if (options.hideWhenEmpty) root.hidden = shown.length === 0;
      const nextSignature = shown.map(game => `${pickKey(game)}:${game.tension ?? ""}:${game.breaking === true}:${game.headline ?? ""}`).join("|");
      if (nextSignature === signature) return;
      signature = nextSignature;
      paint();
    },
  };
}

/**
 * The home page's "headed for a result" band (O3), over the calendar snapshot
 * the grid is built from — no second read. Its picks come from every live
 * event; a board's edition is its round's calendar `edition_id` (the pick's
 * own `HotBoardEntry.edition_id` is unset by the producer today). Text cards,
 * no board artwork: the home page loads none at startup. Hidden when no live
 * game qualifies, and revealed in the same task as the grid above it.
 */
export function createHomeHotBand(): { root: HTMLElement; offer(snapshot: Pick<CalendarSnapshot, "entries" | "topGames">): void } {
  let editions = new Map<string, string | null>();
  const band = createBand({
    className: "hot-band-home",
    titleId: "home-hot-title",
    kicker: "Live · the hottest boards right now",
    title: "Heating up",
    waiting: "",
    empty: "",
    editionOf: (game) => editions.get(game.roundId) ?? null,
    hideWhenEmpty: true,
  });
  return {
    root: band.root,
    offer(snapshot) {
      const live = snapshot.entries.filter((row) => row.state === "active");
      editions = new Map(live.map((row) => [row.entry.roundId, row.editionId]));
      band.update(headedForResult(snapshot.topGames, new Set(editions.keys())));
    },
  };
}
